import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Calendar, Clock, X, ArrowRight, Video, Wrench } from "lucide-react";

const STORAGE_KEY = "kb_dismissed_events";

function getDismissed() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); } catch { return []; }
}
function dismiss(id) {
  const list = Array.from(new Set([...getDismissed(), id]));
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch { /* ignore */ }
}

function timeUntil(iso) {
  const d = new Date(iso) - new Date();
  if (d <= 0) return "now";
  const h = Math.floor(d / 3600000);
  if (h < 24) return `in ${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `in ${days}d`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Sticky top-of-dashboard banner announcing the next non-dismissed upcoming event.
 * Auto-hides if no upcoming events or if user has dismissed every event.
 */
export function UpcomingEventsBanner() {
  const [event, setEvent] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    api.get("/events", { params: { upcoming: true } }).then((r) => {
      const dismissed = new Set(getDismissed());
      // Prefer ongoing > soonest scheduled
      const list = (r.data || []).filter((e) => !dismissed.has(e.id));
      const live = list.find((e) => e.state === "ongoing");
      setEvent(live || list[0] || null);
    }).catch(() => {});
  }, []);

  if (!event || hidden) return null;
  const Icon = event.type === "workshop" ? Wrench : Video;
  const isLive = event.state === "ongoing";
  return (
    <div className={`${isLive ? "bg-rose-700" : "bg-kb-primary"} text-white px-4 lg:px-10 py-2.5 flex items-center gap-3 text-sm`} data-testid="upcoming-events-banner">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] opacity-80 hidden sm:block">{isLive ? "LIVE NOW" : "UPCOMING"}</div>
      <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
      <div className="flex-1 min-w-0">
        <span className="font-medium line-clamp-1">{event.title}</span>
        <span className="opacity-70 ml-2 kb-mono text-[10px] uppercase tracking-wider">
          <Clock className="w-3 h-3 inline -mt-0.5 mr-0.5" />{isLive ? "in progress" : timeUntil(event.scheduled_at)}
          {event.is_free ? " · FREE" : ` · NPR ${event.price_npr?.toLocaleString()}`}
        </span>
      </div>
      <Link to="/events" className="kb-mono text-[10px] uppercase tracking-wider underline opacity-90 hover:opacity-100 inline-flex items-center gap-1 shrink-0" data-testid="banner-event-cta">
        {isLive ? "Join" : "View"} <ArrowRight className="w-3 h-3" />
      </Link>
      <button onClick={() => { dismiss(event.id); setHidden(true); }}
        className="opacity-70 hover:opacity-100 p-1 shrink-0" title="Dismiss" data-testid="banner-event-dismiss">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

/**
 * Compact dashboard card listing upcoming events (next 3) in the user's area.
 */
export function UpcomingEventsCard({ limit = 3, title = "Live events" }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/events", { params: { upcoming: true } })
      .then((r) => setEvents((r.data || []).slice(0, limit)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [limit]);

  if (loading) return null;
  if (events.length === 0) return null;

  return (
    <div className="kb-card p-6" data-testid="upcoming-events-card">
      <div className="flex items-center justify-between">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted inline-flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5" /> {title.toUpperCase()}
        </div>
        <Link to="/events" className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary">VIEW ALL →</Link>
      </div>
      <div className="mt-4 space-y-3">
        {events.map((e) => {
          const Icon = e.type === "workshop" ? Wrench : Video;
          return (
            <Link key={e.id} to="/events" className="block border border-slate-200 rounded-sm p-3 hover:border-kb-primary transition-colors" data-testid={`dash-event-${e.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-kb-primary/10 rounded-sm flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-kb-primary" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-kb-ink line-clamp-1">{e.title}</div>
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-0.5">
                    <Clock className="w-3 h-3 inline" /> {new Date(e.scheduled_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })} · {timeUntil(e.scheduled_at)}
                  </div>
                  {e.host_name && <div className="kb-mono text-[10px] text-kb-ink-light mt-0.5">Host: {e.host_name}</div>}
                </div>
                <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary shrink-0">
                  {e.is_free ? "FREE" : `NPR ${e.price_npr?.toLocaleString()}`}
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
